import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | authed | anon

  const bootstrap = useCallback(async () => {
    if (!getToken()) {
      setStatus("anon");
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setScopes(data.scopes || []);
      setStatus("authed");
    } catch (_e) {
      setToken(null);
      setUser(null);
      setScopes([]);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    // fetch scopes
    try {
      const { data: me } = await api.get("/auth/me");
      setScopes(me.scopes || []);
    } catch (_e) {
      setScopes([]);
    }
    setStatus("authed");
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    setToken(null);
    setUser(null);
    setScopes([]);
    setStatus("anon");
  }, []);

  return (
    <AuthContext.Provider value={{ user, scopes, status, login, logout, bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
